import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/**
 * Busca usuários de other_users filtrados por subsidiárias
 * @param {string|Array<string>} subsidiaries - Subsidiária(s) para filtrar
 * @returns {Promise<Array>} Array de usuários {email, name, subsidiaries, ...}
 */
export const getUserBySubsidiaries = async (subsidiaries) => {
  try {
    const subsidiariesArray = Array.isArray(subsidiaries)
      ? subsidiaries
      : [subsidiaries];

    const otherUsersRef = doc(db, "regional_enterprises", "other_users");
    const otherUsersDoc = await getDoc(otherUsersRef);

    if (!otherUsersDoc.exists()) {
      console.warn(
        "[getUsersBySubsidiaries] Documento other_users não encontrado"
      );
      return [];
    }
    const users = otherUsersDoc.data().users || {};
    const filteredUsers = Object.values(users)
      .filter((user) =>
        user.subsidiaries?.some((sub) => subsidiariesArray.includes(sub))
      )
      .map((user) => ({
        ...user,
        domain: "other_users",
      }));
    return filteredUsers;
  } catch (error) {
    console.error("[getUsersBySubsidiaries] Erro:", error);
    return [];
  }
};

/**
 * Busca domínios de múltiplas subsidiárias
 * @param {string|Array<string>} subsidiaries - ID(s) das subsidiárias
 * @returns {Promise<Array<string>>} Array de domínios únicos
 */

export const getDomainsBySubsidiaries = async (subsidiaries) => {
  try {
    const subsidiariesArray = Array.isArray(subsidiaries)
      ? subsidiaries
      : [subsidiaries];

    const allDomains = [];

    for (const subsidiary of subsidiariesArray) {
      if (typeof subsidiary !== "string" || !subsidiary) {
        continue;
      }

      const subsidiaryRef = doc(db, "subsidiary", subsidiary);
      const subsidiaryDoc = await getDoc(subsidiaryRef);

      if (subsidiaryDoc.exists()) {
        const domains = subsidiaryDoc.data().domains || [];
        if (Array.isArray(domains)) {
          allDomains.push(...domains);
        }
      }
    }

    return [...new Set(allDomains)];
  } catch (error) {
    console.error("[getDomainsBySubsidiaries] Erro:", error);
    return [];
  }
};

/**
 * Busca todos os usuários de todos os domínios
 * @param {Object} options - Opções de filtragem
 * @param {boolean} options.includeDomain - Se true, adiciona campo 'domain' a cada usuário
 * @param {boolean} options.sorted - Se true, ordena alfabeticamente por nome
 * @returns {Promise<Array>} Array de usuários
 */

export const getAllUsersFromEnterprises = async (options = {}) => {
  try {
    const { includeDomain = false, sorted = false } = options;

    const enterprisesRef = collection(db, "regional_enterprises");
    const snapshot = await getDocs(enterprisesRef);

    const allUsers = [];

    snapshot.forEach((docSnapshot) => {
      const docData = docSnapshot.data();
      const usersMap = docData.users || {};

      Object.entries(usersMap).forEach(([email, userData]) => {
        const user = {
          email,
          ...userData,
        };

        if (includeDomain) {
          user.domain = docSnapshot.id;
        }

        allUsers.push(user);
      });
    });

    if (sorted) {
      allUsers.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    }

    return allUsers;
  } catch (error) {
    console.error("[getAllUsersFromEnterprises] Erro:", error);
    return [];
  }
};

/**
 * Extrai o domínio de um email
 * @param {string} email - Email do usuário
 * @returns {string} Domínio extraído
 */

export const extractDomainFromEmail = (email) => {
  if (!email || typeof email !== "string") return "";

  return email.substring(email.lastIndexOf("@") + 1);
};

/**
 * Ordena usuários alfabeticamente por nome
 * @param {Array} users - Array de usuários
 * @returns {Array} Array ordenado
 */
export const sortUsersAlphabetically = (users) => {
  if (!Array.isArray(users)) return [];

  return [...users].sort((a, b) => {
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
};

/**
 * Busca o documento do usuário (primeiro por domínio, depois other_users)
 * @param {string} email - Email do usuário
 * @returns {Promise<Object|null>} { docRef, userData, usersMap, source, domain } ou null
 */
export const getUserDocument = async (email) => {
  try {
    const userDomain = extractDomainFromEmail(email);

    const domainRef = doc(db, "regional_enterprises", userDomain);
    const domainDoc = await getDoc(domainRef);

    if (domainDoc.exists()) {
      const usersMap = domainDoc.data().users || {};
      if (usersMap[email]) {
        return {
          docRef: domainRef,
          userData: usersMap[email],
          usersMap,
          source: "domain",
          domain: userDomain,
        };
      }
    }

    const otherUsersRef = doc(db, "regional_enterprises", "other_users");
    const otherUsersDoc = await getDoc(otherUsersRef);

    if (otherUsersDoc.exists()) {
      const usersMap = otherUsersDoc.data().users || {};
      if (usersMap[email]) {
        return {
          docRef: otherUsersRef,
          userData: usersMap[email],
          usersMap,
          source: "other_users",
          domain: userDomain,
        };
      }
    }

    console.warn(`[getUserDocument] Usuário ${email} não encontrado`);
    return null;
  } catch (error) {
    console.error("[getUserDocument] Erro:", error);
    return null;
  }
};

/**
 * Busca usuários de domínios específicos
 * @param {Array<string>} domains - Array de domínios
 * @returns {Promise<Array>} Array de usuários com campo 'domain'
 */
export const getUserByDomains = async (domains) => {
  try {
    if (!Array.isArray(domains) || domains.length === 0) {
      return [];
    }

    const allUsers = [];

    for (const domain of domains) {
      const domainRef = doc(db, "regional_enterprises", domain);
      const domainDoc = await getDoc(domainRef);

      if (domainDoc.exists()) {
        const usersMap = domainDoc.data().users || {};
        Object.entries(usersMap).forEach(([email, userData]) => {
          allUsers.push({
            email,
            domain,
            ...userData,
          });
        });
      }
    }
    return allUsers;
  } catch (error) {
    console.error("[getUsersByDomains] Erro:", error);
    return [];
  }
};

/**
 * Atualiza campos de um usuário no Firestore
 * @param {string} email - Email do usuário
 * @param {Object} updates - Objeto com campos a atualizar
 * @returns {Promise<boolean>} true se atualizou com sucesso
 */
export const updateUserField = async (email, updates) => {
  try {
    const userDoc = await getUserDocument(email);

    if (!userDoc) {
      console.warn(`[updateUserFields] Usuário ${email} não encontrado`);
      return false;
    }

    const { docRef, usersMap } = userDoc;

    usersMap[email] = {
      ...usersMap[email],
      ...updates,
    };

    await updateDoc(docRef, { users: usersMap });
    console.log(`[updateUserFields] Usuário ${email} atualizado com sucesso`);
    return true;
  } catch (error) {
    console.error("[updateUserFields] Erro:", error);
    return false;
  }
};

/**
 * Filtra usuários baseado em permissões de admin e termo de busca
 * @param {Array} users - Array de usuários
 * @param {Object} options - Opções de filtragem
 * @param {string} options.userRole - Papel do admin (admin_global/admin_local)
 * @param {Array<string>} options.permittedDomains - Domínios permitidos
 * @param {Array} options.subsidiariesUsers - Usuários de subsidiárias
 * @param {string} options.searchTerm - Termo de busca
 * @param {string} options.moduleId - ID do módulo (opcional, para filtrar por módulo)
 * @param {boolean} options.excludeUsersInModule - Se true, exclui usuários já no módulo
 * @param {Array<string>} options.userSubsidiaries - Subsidiárias do admin (para filtrar admin_local)
 * @returns {Array} Array de usuários filtrados
 */
export const filterUsersByPermissions = (users, options = {}) => {
  const {
    userRole,
    permittedDomains = [],
    subsidiariesUsers = [],
    searchTerm = "",
    moduleId = null,
    excludeUsersInModule = false,
    userSubsidiaries = [],
  } = options;

  return users
    .filter((user) => {
      if (moduleId) {
        const isInModule = user.accessibleModules?.some(
          (module) => module.id === moduleId
        );

        if (excludeUsersInModule && isInModule) return false;
        if (!excludeUsersInModule && !isInModule) return false;
      }

      if (userRole === "admin_global") {
        return true;
      }

      // Filtro por subsidiária para admin_local
      if (userRole === "admin_local" && userSubsidiaries.length > 0) {
        // Verifica se o usuário tem pelo menos UMA subsidiária em comum com o admin
        const hasCommonSubsidiary = user.subsidiaries?.some((userSub) =>
          userSubsidiaries.includes(userSub)
        );

        if (!hasCommonSubsidiary) {
          return false;
        }
      }

      const userDomain = extractDomainFromEmail(user.email);
      const isInPermittedDomains = permittedDomains.includes(userDomain);
      const isInSubsidiariesUsers = subsidiariesUsers.some(
        (subsidiaryUser) => subsidiaryUser.email === user.email
      );

      return isInPermittedDomains || isInSubsidiariesUsers;
    })
    .filter((user) => {
      if (!searchTerm) return true;

      const term = searchTerm.toLowerCase();
      return (
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    });
};
